import {
    all, call, put, select, takeEvery, takeLatest,
} from 'redux-saga/effects';
import * as Paho from 'paho-mqtt';

import { LocationState } from 'history';
import { LOCATION_CHANGE, LocationChangeAction } from 'connected-react-router';

import { ContentTypes, CustomFieldDefinitionTypes, qtiTypes } from 'common/store/common/constants';
import commonApiService from 'common/services/api/commonApiService';
import urlUtil from 'common/util/url/urlUtil';
import { SessionState } from 'common/store/session/reducer';

import { Alert, AlertLevel } from 'common/components/notifications/types';
import { pushAlert } from 'common/components/notifications/actions';
import {
    sectionCustomFieldDefinitionsLoaded,
    questionCustomFieldDefinitionsLoaded,
    PublishStatusChanged,
    publishStatusChangePushed,
    dateableChangePushed,
    EditCustomFieldsMetadata,
    customMetadataFieldChangePushed,
    customMetadataFieldsLoaderChanged,
    TypeKeys as CustomFieldsTypeKeys,
    DateableChanged,
    customMetadataFieldInContextChangePushed,
    customFieldsDefinitionKitChange,
    questionSetCustomFieldDefinitionsLoaded,
    contentSoundCustomFieldDefinitionsLoaded,
} from './actions';

import { updateCurrentItemContext, updateCurrentQtiContextItem } from '../context/actions';
import apiService from '../../services/apiService';
import awsIOTService from '../../services/AWSIOTService';

import storeManager from '../index';
import { RootState } from '../rootReducer';
import { ContextState } from '../context/reducer';
import { ContextUrlSearchStringObject, ContextTypeKeys } from '../context/types';
import customFieldsService from '../../services/metadata/customFieldsService';
import { CustomFieldsDefinitionState } from './reducer';
import detailsPanelService from '../../services/metadata/detailsPanelService';
import contextService from '../../services/contextService';

const getContextState = (state: RootState): ContextState => state.context;
const getCustomFieldDefinitionState = (state: RootState): CustomFieldsDefinitionState => state.customFieldsDefinition;
const getSessionState = (state: RootState): SessionState => state.session;

export function* fetchCustomFieldDefinitions(locationChangeAction: LocationChangeAction<LocationState>) {
    const {
        payload: {
            isFirstRendering,
            location: { search },
        },
    } = locationChangeAction;
    if (isFirstRendering) {
        const { contentType, itemBankId } = urlUtil.parseLocationSearchString(search) as ContextUrlSearchStringObject;

        const isItemBankSpecificCustomFieldsEnabled = window.marketplaceProperties
            .ENABLE_ITEM_BANK_SPECIFIC_OBJECT_CUSTOM_FIELDS_AND_ALLOWED_VALUES;
        const effectiveItemBankId = isItemBankSpecificCustomFieldsEnabled ? itemBankId : undefined;

        if (!Object.values(qtiTypes).includes(contentType)) {
            const {
                objectCustomFieldDefinitions: contentBlockCustomFieldDefinitions,
            } = yield call(commonApiService.fetchCustomFieldDefinitions, CustomFieldDefinitionTypes.SECTION, effectiveItemBankId);
            yield put(sectionCustomFieldDefinitionsLoaded(contentBlockCustomFieldDefinitions));
        }
        const {
            objectCustomFieldDefinitions: questionCustomFieldDefinitions,
        } = yield call(commonApiService.fetchCustomFieldDefinitions, CustomFieldDefinitionTypes.QUESTION, effectiveItemBankId);
        yield put(questionCustomFieldDefinitionsLoaded(questionCustomFieldDefinitions));
        const {
            objectCustomFieldDefinitions: questionSetCustomFieldDefinitions,
        } = yield call(commonApiService.fetchCustomFieldDefinitions, CustomFieldDefinitionTypes.QUESTION_SET, effectiveItemBankId);
        yield put(questionSetCustomFieldDefinitionsLoaded(questionSetCustomFieldDefinitions));
        const {
            objectCustomFieldDefinitions: contentSoundCustomFieldDefinitions,
        } = yield call(commonApiService.fetchCustomFieldDefinitions, CustomFieldDefinitionTypes.CONTENT_SOUND, effectiveItemBankId);
        yield put(contentSoundCustomFieldDefinitionsLoaded(contentSoundCustomFieldDefinitions));
    }
}

export function* setInitialCustomFieldsDefinitionKit() {
    const customFieldsDefinition: CustomFieldsDefinitionState = yield select(getCustomFieldDefinitionState);

    if (customFieldsDefinition.isReviewFields) {
        yield call(setInitialReviewCustomFieldsDefinitionKit);
        return;
    }

    const { currentItemContext, currentQtiContextItem } = yield select(getContextState);

    let contentType = currentItemContext?.contentType;
    const isTypeQuestion = contextService.isContentTypeQuestion<CustomFieldDefinitionTypes>(
        [currentQtiContextItem?.objectType, currentItemContext?.contentType],
        CustomFieldDefinitionTypes.QUESTION
    );
    const { contextContentType, objectType } = currentQtiContextItem || {};
    const isTypeSectionInQS = contextContentType === ContentTypes.QUESTION_SET && objectType === ContentTypes.SECTION;

    if (isTypeQuestion) {
        contentType = CustomFieldDefinitionTypes.QUESTION;
    } else if (isTypeSectionInQS) {
        contentType = CustomFieldDefinitionTypes.SECTION;
    }

    const fields = detailsPanelService.sortFieldsByName(
        'fieldName',
        detailsPanelService.getCustomFieldsDefinition(customFieldsDefinition, contentType)
    );

    const scopedItem = contentType === CustomFieldDefinitionTypes.QUESTION ? currentQtiContextItem : currentItemContext;
    const initialCustomFieldsDefinitionKit = fields?.map(({ fieldDefinitionId, fieldName }) => ({
        value: customFieldsService.getCustomFieldValue(fieldDefinitionId, scopedItem),
        initialValue: customFieldsService.getCustomFieldValue(fieldDefinitionId, scopedItem),
        isDirty: false,
        fieldDefinitionId,
        fieldName,
        hiddenInContent: customFieldsService.getHiddenInContent(fieldDefinitionId, scopedItem),
    }));
    yield put(customFieldsDefinitionKitChange(initialCustomFieldsDefinitionKit));
}

export function* setInitialReviewCustomFieldsDefinitionKit() {
    const { currentItemContext } = yield select(getContextState);
    const customFieldsDefinition: CustomFieldsDefinitionState = yield select(getCustomFieldDefinitionState);
    const { marketplaceProperties } = yield select(getSessionState);

    const contentType = currentItemContext?.contentType;
    const fields = customFieldsService.getFilteredCustomFields(contentType, customFieldsDefinition, marketplaceProperties, true);

    const initialCustomFieldsDefinitionKit = fields?.map(({ fieldDefinitionId, fieldName }) => ({
        value: customFieldsService.getCustomFieldValue(fieldDefinitionId, currentItemContext),
        initialValue: customFieldsService.getCustomFieldValue(fieldDefinitionId, currentItemContext),
        isDirty: false,
        fieldDefinitionId,
        fieldName,
    }));
    yield put(customFieldsDefinitionKitChange(initialCustomFieldsDefinitionKit));
}

function* fetchContextData() {
    const { contextId } = yield select(getContextState);
    if (contextId) {
        yield put(updateCurrentItemContext());
        yield put(updateCurrentQtiContextItem());
    }
}

function* fetchContextItemData() {
    const { qtiContextItemId } = yield select(getContextState);
    if (qtiContextItemId) {
        yield put(updateCurrentQtiContextItem());
    }
}

function* postPublishStatusChanged(action: PublishStatusChanged) {
    const { currentQtiContextItem } = yield select(getContextState);
    const { contentItemId, publishStatus } = action.payload;
    const response = yield call(apiService.setPublishStatus, publishStatus, contentItemId);
    const isQtiItem = currentQtiContextItem?.objectId === contentItemId;
    if (response.success && response.pushChannel) {
        const topic = awsIOTService.buildTopic(response.pushChannel);
        const callback = (message: Paho.Message) => {
            if (message) {
                storeManager.dispatch(publishStatusChangePushed(isQtiItem));
            }
        };

        yield call(awsIOTService.subscribeToTopic, topic, callback.bind(awsIOTService));
    }
}

function* postDateableChanged(action: DateableChanged) {
    const { currentQtiContextItem } = yield select(getContextState);
    const { contentItemId, itemProperty, value } = action.payload;
    const response = yield call(apiService.setDateableValue, contentItemId, itemProperty, value);
    const isQtiItem = currentQtiContextItem?.objectId === contentItemId;
    if (response.success && response.pushChannel) {
        const topic = awsIOTService.buildTopic(response.pushChannel);
        const callback = (message: Paho.Message) => {
            if (message) {
                storeManager.dispatch(dateableChangePushed(isQtiItem));
            }
        };

        yield call(awsIOTService.subscribeToTopic, topic, callback.bind(awsIOTService));
    }
}

function* updateContextItem(action: any) {
    const skipLockModal = action.type === CustomFieldsTypeKeys.DATEABLE_CHANGED_PUSHED
        || action.type === CustomFieldsTypeKeys.PUBLISH_STATUS_CHANGE_PUSHED;
    if (action.payload) {
        yield storeManager.dispatch(updateCurrentQtiContextItem());
    } else {
        yield storeManager.dispatch(updateCurrentItemContext(skipLockModal));
    }
}

function* postEditCustomFieldsMetadata(action: EditCustomFieldsMetadata) {
    const {
        contentItemId,
        customFieldUpdates,
        contextContentItemId,
        callback: resetCallback,
    } = action.payload;

    storeManager.dispatch(customMetadataFieldsLoaderChanged(true));

    const response = yield call(
        apiService.editObjectCustomFieldsMetadata,
        [contentItemId],
        customFieldUpdates,
        contextContentItemId,
    );
    if (response.success) {
        const callbackItems = (message: Paho.Message) => {
            if (message) {
                storeManager.dispatch(customMetadataFieldChangePushed());
                storeManager.dispatch(customMetadataFieldsLoaderChanged(false));
            }
        };
        const callbackQTIContext = (message: Paho.Message) => {
            if (message) {
                storeManager.dispatch(customMetadataFieldInContextChangePushed());
            }
        };
        resetCallback();
        yield call(
            awsIOTService.subscribeToTopic,
            awsIOTService.buildTopic(response.pushChannel_itemsIndex_context || response.pushChannel_itemsIndex),
            callbackItems.bind(awsIOTService)
        );
        if (contextContentItemId) {
            yield call(
                awsIOTService.subscribeToTopic,
                awsIOTService.buildTopic(response.pushChannel_contextIndex_context),
                callbackQTIContext.bind(awsIOTService)
            );
        }
    } else {
        storeManager.dispatch(customMetadataFieldsLoaderChanged(false));
        const alert: Alert = {
            key: 'author.customFieldsDefinition.error.MetadataNotSaved.title',
            info: 'author.customFieldsDefinition.error.MetadataNotSaved.description',
            level: AlertLevel.ERROR,
            payload: {},
        };
        yield put(pushAlert(alert));
    }
}

function* listenForPublishStatusChanged() {
    yield takeEvery(CustomFieldsTypeKeys.PUBLISH_STATUS_CHANGED, postPublishStatusChanged);
}
function* listenForPublishStatusPushed() {
    yield takeEvery(CustomFieldsTypeKeys.PUBLISH_STATUS_CHANGE_PUSHED, updateContextItem);
}

function* listenForDateableChanged() {
    yield takeEvery(CustomFieldsTypeKeys.DATEABLE_CHANGED, postDateableChanged);
}

function* listenForDateablePushed() {
    yield takeEvery(CustomFieldsTypeKeys.DATEABLE_CHANGED_PUSHED, updateContextItem);
}

function* listenForEditCustomFieldsMetadata() {
    yield takeEvery(CustomFieldsTypeKeys.EDIT_CUSTOM_FIELDS_METADATA, postEditCustomFieldsMetadata);
}

function* listenForContentItemIdUpdated() {
    yield takeEvery([
        CustomFieldsTypeKeys.PUBLISH_STATUS_CHANGE_PUSHED,
        CustomFieldsTypeKeys.CUSTOM_METADATA_FIELD_CHANGE_PUSHED,
        CustomFieldsTypeKeys.DATEABLE_CHANGED_PUSHED,
    ], fetchContextData);
}

function* listenForContextItemIdUpdated() {
    yield takeEvery([
        CustomFieldsTypeKeys.CUSTOM_METADATA_FIELD_IN_CONTEXT_CHANGE_PUSHED,
    ], fetchContextItemData);
}

function* watchRouterLocationChange() {
    yield takeEvery(LOCATION_CHANGE, fetchCustomFieldDefinitions);
}

function* watchCustomFieldDefinitionsLoaded() {
    yield takeLatest([
        ContextTypeKeys.SET_CURRENT_QTI_CONTEXT_ITEM,
        ContextTypeKeys.SET_CURRENT_ITEM_CONTEXT,
    ], setInitialCustomFieldsDefinitionKit);
}

function* watchReviewCustomFieldDefinitionsLoaded() {
    yield takeLatest(CustomFieldsTypeKeys.SET_INITIAL_REVIEW_CUSTOM_FIELDS, setInitialReviewCustomFieldsDefinitionKit);
}

export function* rootCustomFieldsDefinitionSaga() {
    yield all([
        watchRouterLocationChange(),
        listenForPublishStatusChanged(),
        listenForDateableChanged(),
        listenForContentItemIdUpdated(),
        listenForContextItemIdUpdated(),
        listenForEditCustomFieldsMetadata(),
        watchCustomFieldDefinitionsLoaded(),
        watchReviewCustomFieldDefinitionsLoaded(),
        listenForPublishStatusPushed(),
        listenForDateablePushed(),
    ]);
}
